import { useEffect, useMemo, useRef } from "react";

import "./ImagesLoader.scss";

export default function ImagesLoader({
    images,
    setImages,
    folder,
    amount,
    extension,
    qualitySteps,
    checkIfHighByDefault,
    setLoadedPart,
    onFullLoad,
    onPhaseComplete,
}) {
    const onFullLoadRef = useRef(onFullLoad);
    const onPhaseCompleteRef = useRef(onPhaseComplete);
    const checkIfHighByDefaultRef = useRef(checkIfHighByDefault);

    // Keep refs updated
    useEffect(() => {
        onFullLoadRef.current = onFullLoad;
        onPhaseCompleteRef.current = onPhaseComplete;
        checkIfHighByDefaultRef.current = checkIfHighByDefault;
    }, [onFullLoad, onPhaseComplete, checkIfHighByDefault]);

    // Calculate phase indices for progressive loading
    const phases = useMemo(() => {
        const phase1 = [];
        const phase2 = [];
        const phase3 = [];

        const priorityFrames = new Set();
        const checkFn = checkIfHighByDefaultRef.current;
        if (checkFn) {
            for (let i = 1; i <= amount; i++) {
                if (checkFn(i)) {
                    priorityFrames.add(i);
                    phase1.push(i);
                }
            }
        }

        for (let i = 1; i <= amount; i += 4) {
            if (!priorityFrames.has(i)) {
                phase1.push(i);
            }
        }

        for (let i = 3; i <= amount; i += 4) {
            if (!priorityFrames.has(i)) {
                phase2.push(i);
            }
        }

        for (let i = 2; i <= amount; i += 2) {
            if (!priorityFrames.has(i)) {
                phase3.push(i);
            }
        }

        return { phase1, phase2, phase3 };
    }, [amount]);

    // Main loading effect — all loading logic lives inside so each run
    // captures its own `cancelled` closure.
    useEffect(() => {
        let cancelled = false;
        let loadedCount = 0;
        const loadedSet = new Set();
        // Store HTMLImageElement objects, not just URLs — keeping them alive
        // prevents the browser from GC'ing decoded bitmaps, so the <img> tag
        // in Frame gets instant cache hits instead of re-fetching from network.
        const imagesArr = new Array(amount).fill(null);

        setLoadedPart(0);

        const loadImage = (idx) => {
            if (cancelled) return Promise.resolve(null);

            return new Promise((resolve) => {
                const img = new Image();

                img.onload = () => {
                    if (cancelled) {
                        resolve(null);
                        return;
                    }
                    if (!loadedSet.has(idx)) {
                        loadedSet.add(idx);
                        loadedCount++;
                        setLoadedPart(loadedCount / amount);
                    }
                    resolve(img);
                };

                img.onerror = () => {
                    if (cancelled) {
                        resolve(null);
                        return;
                    }
                    console.error(`Error loading image ${idx}`);
                    resolve(null);
                };

                img.src = `https://alaqtar-demo5-api.visengine.dev/${folder}/${qualitySteps}/${idx}${extension}`;
            });
        };

        const loadWithConcurrency = async (indices, limit = 4) => {
            if (!indices.length) return [];

            const results = new Array(indices.length).fill(null);
            let cursor = 0;

            const workers = new Array(Math.min(limit, indices.length))
                .fill(0)
                .map(async () => {
                    while (cursor < indices.length && !cancelled) {
                        const myIndex = cursor++;
                        const idx = indices[myIndex];
                        const imgEl = await loadImage(idx);
                        results[myIndex] = imgEl;
                    }
                });

            await Promise.all(workers);
            return results;
        };

        const loadPhaseBatch = async (indices) => {
            const loadedImages = await loadWithConcurrency(indices, 4);

            if (cancelled) return;

            indices.forEach((idx, i) => {
                if (loadedImages[i]) {
                    imagesArr[idx - 1] = loadedImages[i];
                }
            });

            setImages([...imagesArr]);
        };

        (async () => {
            // Phase 1: priority frames + every 4th frame
            await loadPhaseBatch(phases.phase1);
            if (cancelled) return;
            if (onPhaseCompleteRef.current) {
                onPhaseCompleteRef.current(1, 3);
            }

            // Phase 2: every 4th frame starting from 3
            await loadPhaseBatch(phases.phase2);
            if (cancelled) return;
            if (onPhaseCompleteRef.current) {
                onPhaseCompleteRef.current(2, 3);
            }

            // Phase 3: remaining frames
            await loadPhaseBatch(phases.phase3);
            if (cancelled) return;
            if (onPhaseCompleteRef.current) {
                onPhaseCompleteRef.current(3, 3);
            }

            // All phases complete
            if (onFullLoadRef.current) {
                onFullLoadRef.current();
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [folder, amount, extension, qualitySteps, phases, setImages, setLoadedPart]);

    // This component no longer renders img tags - loading is done via Image objects
    return <div className="images-loader" />;
}
